import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, TableBody, TableCell, TableHead,
  TableRow, Chip, Grid, Button, Tooltip, Alert, CircularProgress, TextField, MenuItem
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import { formatAmount } from '../../utils/money';
import ScrollTable from '../ScrollTable';

// Grouped without a currency prefix — these tables carry the currency in their heading.
const fmt = (n) => formatAmount(n);

const STATUS = {
  overdue: { label: 'Overdue', color: 'error' },
  due_today: { label: 'Due today', color: 'warning' },
  upcoming: { label: 'Upcoming', color: 'info' },
  scheduled: { label: 'Scheduled', color: 'default' },
  paid: { label: 'Paid', color: 'success' }
};

// Overdue first, then due, then upcoming — the order the accountant needs to act in.
const ORDER = ['overdue', 'due_today', 'upcoming', 'scheduled', 'paid'];

const LandlordPayoutsDue = ({ onPay }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState('');
  const [landlordId, setLandlordId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [landlords, setLandlords] = useState([]);
  const [properties, setProperties] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/landlord-payments/due', {
        params: {
          ...(landlordId ? { landlord: landlordId } : {}),
          ...(propertyId ? { property: propertyId } : {})
        }
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load payouts due');
    } finally {
      setLoading(false);
    }
  }, [landlordId, propertyId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get('/landlords')
      .then((r) => setLandlords((Array.isArray(r.data) ? r.data : r.data.data || []).filter((l) => l.isActive)))
      .catch(() => setLandlords([]));
  }, []);

  // Scoped to the chosen landlord, so a property left selected from a previous one cannot
  // filter to a building they do not own and return an empty schedule.
  const chooseLandlord = (id) => {
    setLandlordId(id);
    setPropertyId('');
    if (!id) { setProperties([]); return; }
    axios.get(`/landlord-statements/${id}/properties`)
      .then((r) => setProperties(r.data.properties || []))
      .catch(() => setProperties([]));
  };

  const runReminders = async (dryRun) => {
    try {
      setRunning(true);
      const res = await axios.post('/landlord-payments/reminders/run', { dryRun });
      toast.success(res.data.message);
      if (!dryRun) load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Reminder run failed');
    } finally {
      setRunning(false);
    }
  };

  if (loading && !data) return <CircularProgress size={24} />;
  if (!data) return null;

  const rows = [...data.rows]
    .filter((r) => !filter || r.status === filter)
    .sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status) || a.daysUntil - b.daysUntil);

  const card = (label, value, color) => (
    <Grid item xs={6} sm={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" color={color}>{value}</Typography>
      </Paper>
    </Grid>
  );

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Each property has its own pay day, so a landlord with several buildings appears once
        per property. <strong>Expected</strong> comes from the rent set on the tenancies less
        the agency fee; <strong>per ledger</strong> is what the property has actually earned.
        Where they differ, rent has usually not been collected — check before paying.
      </Alert>

      <Grid container spacing={2} mb={2}>
        {card('Overdue', data.summary.overdue, 'error.main')}
        {card('Due today', data.summary.dueToday, 'warning.main')}
        {card('Upcoming', data.summary.upcoming, 'info.main')}
        {card('Still to pay this month', fmt(data.summary.expectedNetOutstanding))}
      </Grid>

      <Box mb={2} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select size="small" label="Landlord" value={landlordId} sx={{ minWidth: 180 }}
          onChange={(e) => chooseLandlord(e.target.value)}
        >
          <MenuItem value="">All landlords</MenuItem>
          {landlords.map((l) => <MenuItem key={l._id} value={l._id}>{l.name}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Property" value={propertyId} sx={{ minWidth: 200 }}
          onChange={(e) => setPropertyId(e.target.value)}
          disabled={!landlordId}
        >
          <MenuItem value="">All properties</MenuItem>
          {properties.map((p) => (
            <MenuItem key={p._id || p.propertyId} value={p._id || p.propertyId}>
              {p.propertyName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select size="small" label="Status" value={filter} sx={{ minWidth: 160 }}
          onChange={(e) => setFilter(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {ORDER.map((s) => <MenuItem key={s} value={s}>{STATUS[s].label}</MenuItem>)}
        </TextField>
        {(landlordId || propertyId || filter) && (
          <Button size="small" onClick={() => { chooseLandlord(''); setFilter(''); }}>Clear</Button>
        )}
        <Button variant="outlined" onClick={() => runReminders(true)} disabled={running}>
          Preview Reminders
        </Button>
        <Tooltip title="Reminders also go out automatically each morning. Each property gets one per stage per month.">
          <span>
            <Button variant="outlined" onClick={() => runReminders(false)} disabled={running}>
              Send Reminders Now
            </Button>
          </span>
        </Tooltip>
        <Button onClick={load} disabled={loading}>Refresh</Button>
      </Box>

      <ScrollTable containerProps={{ component: Paper, variant: 'outlined', sx: { overflowX: 'auto' } }} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Landlord</TableCell>
              <TableCell>Property</TableCell>
              <TableCell>Pay day</TableCell>
              <TableCell>Due</TableCell>
              <TableCell align="right">Rent</TableCell>
              <TableCell align="right">Fee</TableCell>
              <TableCell align="right">Expected</TableCell>
              <TableCell align="right">Per ledger</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} sx={{ color: 'text.secondary' }}>
                  Nothing to show. Properties appear here once they have a landlord and a pay day.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.propertyId} hover>
                <TableCell>
                  <Chip
                    size="small" variant="outlined"
                    label={STATUS[r.status]?.label || r.status}
                    color={STATUS[r.status]?.color || 'default'}
                  />
                </TableCell>
                <TableCell>{r.landlordName}</TableCell>
                <TableCell>{r.propertyName}</TableCell>
                <TableCell>{r.paymentDayLabel}</TableCell>
                <TableCell>
                  {new Date(r.dueDate).toLocaleDateString()}
                  {r.status !== 'paid' && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {r.daysUntil === 0 ? 'today'
                        : r.daysUntil > 0 ? `in ${r.daysUntil} day(s)`
                          : `${Math.abs(r.daysUntil)} day(s) late`}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{fmt(r.expectedGross)}</TableCell>
                <TableCell align="right">
                  {fmt(r.managementFee)}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {/* "Mixed" when the building's units were negotiated separately —
                        showing one of their rates would read as the whole property's. */}
                    {r.feeBasis || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(r.expectedNet)}</TableCell>
                <TableCell align="right">
                  {fmt(r.actualNet)}
                  {/* Only surfaced when the two disagree — on every row it would be noise,
                      here it is the most useful thing on the line. */}
                  {Math.abs(r.variance) >= 1 && (
                    <Tooltip title={`The ledger differs from the expected figure by ${fmt(r.variance)}. Usually rent not yet collected, or a cost recharged to the landlord.`}>
                      <Typography variant="caption" display="block" color="warning.main">
                        <WarningAmberIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} /> {fmt(r.variance)}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  {r.status === 'paid' ? (
                    <Tooltip title={`Settled by ${r.settledBy?.paymentNumber}`}>
                      <Typography variant="caption" color="text.secondary">
                        {r.settledBy?.paymentNumber}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Button size="small" onClick={() => onPay?.(r)}>Pay</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ScrollTable>

      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        Reminders are sent to the addresses configured under Company Information → Accounting
        Policy, or to admin users if none are set.
      </Typography>
    </Box>
  );
};

export default LandlordPayoutsDue;
